import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { LogOut, Menu, X, User, Calendar, LayoutDashboard } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useProfilesRealtime } from '@/hooks/useProfilesRealtime';

export default function Navbar() {
  const { user, isAdmin, isStaff, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  useProfilesRealtime();

  const { data: profileName } = useQuery({
    queryKey: ['navbar-profile', user?.id],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('user_id', user.id)
        .maybeSingle();
      const name = data?.full_name?.split(' ')[0] || user.user_metadata?.full_name?.split(' ')[0];
      return name || null;
    },
  });

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleServicesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#services');
    }
    setOpen(false);
  };

  return (
    <>
      {profileName && (
        <div className="bg-primary/10 text-primary text-center py-2 text-sm font-medium tracking-wide">
          Bem-vinda, {profileName}! ✨
        </div>
      )}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between">
        <Link to="/" className="font-display text-lg sm:text-xl font-bold text-primary tracking-wide">
          Bonjour Sobrancelhas
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-4 lg:gap-6">
          {!isAdmin && !isStaff && (
            <>
              <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Início</Link>
              <a href="/#services" onClick={handleServicesClick} className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Serviços</a>
            </>
          )}
          {user ? (
            <>
              {!isAdmin && !isStaff && (
                <>
                  <Link to="/booking" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                    <Calendar className="h-4 w-4" /> Agendar
                  </Link>
                  <Link to="/meus-dados" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                    <User className="h-4 w-4" /> Meus Dados
                  </Link>
                </>
              )}
              {(isAdmin || isStaff) && (
                <Link to="/admin" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                  <LayoutDashboard className="h-4 w-4" /> {isAdmin ? 'Admin' : 'Painel'}
                </Link>
              )}
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="min-h-[44px]">
                <LogOut className="h-4 w-4" /> Sair
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate('/auth')} className="min-h-[44px]">
                <User className="h-4 w-4" /> Entrar
              </Button>
              <Button variant="hero" size="sm" onClick={() => navigate('/auth?tab=signup')} className="min-h-[44px]">
                Cadastrar
              </Button>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setOpen(!open)}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-border bg-background p-4 space-y-1">
          {!isAdmin && !isStaff && (
            <>
              <Link to="/" onClick={() => { setOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="block text-sm font-medium min-h-[44px] flex items-center px-2 rounded-md hover:bg-accent">Início</Link>
              <a href="/#services" onClick={handleServicesClick} className="block text-sm font-medium min-h-[44px] flex items-center px-2 rounded-md hover:bg-accent">Serviços</a>
            </>
          )}
          {user ? (
            <>
              {!isAdmin && !isStaff && <Link to="/booking" onClick={() => setOpen(false)} className="block text-sm font-medium min-h-[44px] flex items-center px-2 rounded-md hover:bg-accent">Agendar</Link>}
              {!isAdmin && !isStaff && <Link to="/meus-dados" onClick={() => setOpen(false)} className="block text-sm font-medium min-h-[44px] flex items-center px-2 rounded-md hover:bg-accent">Meus Dados</Link>}
              {(isAdmin || isStaff) && <Link to="/admin" onClick={() => setOpen(false)} className="block text-sm font-medium min-h-[44px] flex items-center px-2 rounded-md hover:bg-accent">{isAdmin ? 'Admin' : 'Painel'}</Link>}
              <Button variant="ghost" size="sm" onClick={() => { handleSignOut(); setOpen(false); }} className="w-full justify-start min-h-[44px]">Sair</Button>
            </>
          ) : (
            <div className="flex flex-col gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => { navigate('/auth'); setOpen(false); }} className="w-full min-h-[44px]">Entrar</Button>
              <Button variant="hero" size="sm" onClick={() => { navigate('/auth?tab=signup'); setOpen(false); }} className="w-full min-h-[44px]">Cadastrar</Button>
            </div>
          )}
        </div>
      )}
      </nav>
    </>
  );
}
